import type {
  GetSalonSuggestions,
  DeleteTierPeriod,
  GetAdminBillingDunning,
  GetAdminBillingOverview,
  GetAdminBillingTrends,
  GetAdminCardFailureCount,
  GetAdminCardFailures,
  DeleteTimeOffs,
  GetClientAppointmentExport,
  GetClientLocations,
  PostAdminRecoverLocation,
  GetClientBilling,
  GetClientDetails,
  GetClientOverdueBanner,
  PutClientOverdueBanner,
  AdminGetClientFeatures,
  GetClientForm,
  GetClientForms,
  GetClientImpersonationSessions,
  GetImpersonationSessionActivity,
  GetClientList,
  GetClientNotifications,
  GetClientBulkNotifications,
  GetClientBulkNotificationsDay,
  GetClientBulkNotificationDetail,
  PostClientBulkNotificationsResend,
  PutClientNotifications,
  GetClientInvoices,
  GetPartners,
  GetPricingTier,
  GetPricingTierById,
  LimeAdminGetTimeOffs,
  GetTotalPrice,
  GetBillingChangeLog,
  PutBillingChangeSetNote,
  PostActivateBilling,
  PostCloseClient,
  PostBackdatedClose,
  PostCloseDate,
  PostClientForm,
  PostClientLogo,
  PostClientTierPeriod,
  PostDeactivateBilling,
  PostDuplicateClientForm,
  PostFormBackgroundImage,
  PostImportCustomers,
  PostImportProducts,
  PostImportServices,
  PostLoginAsAdmin,
  PostValidateServiceData,
  PutClientBilling,
  PutClientDetails,
  PutClientFeatures,
  PutClientForm,
  PutPricingTier,
  PutTierPeriod,
  PutClientUrls,
  DeleteFormBackgroundImage,
  GetClientBillingDetails,
  GetClientNextInvoice,
  GetClientInvoice,
  GetInvoices,
  DeleteClientInvoice,
  DeleteManualInvoice,
  GetManualInvoiceCandidates,
  GetManualInvoiceProposal,
  PostManualInvoice,
  PostRequeueClientInvoice,
  PostRunRecurrentBilling,
  GetEmailActivity,
  PostAdminRecoverUser,
  GetLimeAdminShifts,
  DeleteLimeAdminShifts,
  DeleteClientPartnerId,
  GetLimeAdminClientPromoCodes,
  PostLimeAdminClientPromoCode,
  PutLimeAdminClientPromoCode,
  GetLimeAdminClientAppliedPromoCode,
  PutLimeAdminClientAppliedPromoCode,
  DeleteLimeAdminClientPromoCode,
  GetLimeAdminTaxRegisterSettings,
  GetLimeAdminHubspotDeals,
  PostLimeAdminTaxRegisterSettings,
  PostQuickClientOnboarding,
  GetSilentChurn,
  GetAnchorReport,
  GetBillingGaps,
  GetClientsNeedingManualInvoice,
  GetManualClientsDueCount,
  PutManualInvoiceNote,
  PutBillingGapReview,
  GetPaidSheetRows,
  GetPaidSheetComparison,
  GetPaidSheetBillingPrefill,
  GetPaidSheetActivationPreview,
  PostPaidSheetRefetch,
  PutPaidSheetReview,
  PutPaidSheetNote,
  PostPaidSheetRematch,
  PostPaidSheetSync,
  PostPaidSheetDecisions,
  PostPaidSheetImport,
  GetLimeAdminTagCombinations,
  PostLimeAdminTagCombination,
  DeleteLimeAdminTagCombination,
  PutLimeAdminTagCombinationOrder,
  PostLimeAdminBroadcast,
  GetLimeAdminBroadcasts,
  GetLimeAdminBroadcastById,
  PostCompleteScrape,
  PostResetScrapeAcceptance,
} from "@/server-types";
import { keepPreviousData } from "@tanstack/react-query";
import { del, get, post, put } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";

/**
 * POST /lime-admin/clients
 */
export const useClientList = (body: GetClientList["body"]) => {
  return useCustomQuery({
    queryKey: ["clientList", body],
    queryFn: () => post<GetClientList>("/lime-admin/clients", body),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * GET /lime-admin/silent-churn
 */
export const useSilentChurn = (query: GetSilentChurn["query"]) => {
  return useCustomQuery({
    queryKey: ["silentChurn", query],
    queryFn: () => get<GetSilentChurn>("/lime-admin/silent-churn", query),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * GET /lime-admin/anchor-report
 */
export const useAnchorReport = () => {
  return useCustomQuery({
    queryKey: ["anchorReport"],
    queryFn: () => get<GetAnchorReport>("/lime-admin/anchor-report"),
    refetchOnWindowFocus: false,
  });
};

/**
 * GET /lime-admin/billing-gaps
 */
export const useBillingGaps = (query: GetBillingGaps["query"]) => {
  return useCustomQuery({
    queryKey: ["billingGaps", query],
    queryFn: () => get<GetBillingGaps>("/lime-admin/billing-gaps", query),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * GET /lime-admin/salon-suggestions
 */
export const useSalonSuggestions = () => {
  return useCustomQuery({
    queryKey: ["salonSuggestions"],
    queryFn: () => get<GetSalonSuggestions>("/lime-admin/salon-suggestions"),
  });
};

/**
 * GET /lime-admin/billing-gaps/manual-invoices
 */
export const useClientsNeedingManualInvoice = () => {
  return useCustomQuery({
    queryKey: ["clientsNeedingManualInvoice"],
    queryFn: () =>
      get<GetClientsNeedingManualInvoice>(
        "/lime-admin/billing-gaps/manual-invoices",
      ),
    refetchOnWindowFocus: false,
  });
};

/**
 * GET /lime-admin/billing-gaps/manual-invoices/count
 */
export const useManualClientsDueCount = () => {
  return useCustomQuery({
    queryKey: ["clientsNeedingManualInvoice", "count"],
    queryFn: () =>
      get<GetManualClientsDueCount>(
        "/lime-admin/billing-gaps/manual-invoices/count",
      ),
    refetchOnWindowFocus: false,
  });
};

/**
 * PUT /lime-admin/billing-gaps/manual-invoices/[clientId]/note
 */
export const useUpdateManualInvoiceNote = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: number;
      body: PutManualInvoiceNote["body"];
    }) =>
      put<PutManualInvoiceNote>(
        `/lime-admin/billing-gaps/manual-invoices/${clientId}/note`,
        body,
      ),
  });
};

/**
 * GET /lime-admin/billing/card-failures
 */
export const useAdminCardFailures = () => {
  return useCustomQuery({
    queryKey: ["adminCardFailures"],
    queryFn: () =>
      get<GetAdminCardFailures>("/lime-admin/billing/card-failures"),
    refetchOnWindowFocus: false,
  });
};

/**
 * GET /lime-admin/billing/card-failures/count — for the tab badge, which shows
 * on every god mode page and should not load the whole list.
 */
export const useAdminCardFailureCount = () => {
  return useCustomQuery({
    queryKey: ["adminCardFailures", "count"],
    queryFn: () =>
      get<GetAdminCardFailureCount>("/lime-admin/billing/card-failures/count"),
    refetchOnWindowFocus: false,
  });
};

/**
 * GET /lime-admin/billing/overview — today's KPIs, collection and invoices per month.
 */
export const useAdminBillingOverview = () => {
  return useCustomQuery({
    queryKey: ["adminBillingOverview"],
    queryFn: () => get<GetAdminBillingOverview>("/lime-admin/billing/overview"),
    refetchOnWindowFocus: false,
  });
};

/**
 * GET /lime-admin/billing/overview/trends — monthly metrics from the daily snapshots.
 */
export const useAdminBillingTrends = () => {
  return useCustomQuery({
    queryKey: ["adminBillingOverview", "trends"],
    queryFn: () =>
      get<GetAdminBillingTrends>("/lime-admin/billing/overview/trends"),
    refetchOnWindowFocus: false,
  });
};

/**
 * GET /lime-admin/billing/dunning
 */
export const useAdminBillingDunning = () => {
  return useCustomQuery({
    queryKey: ["adminBillingDunning"],
    queryFn: () => get<GetAdminBillingDunning>("/lime-admin/billing/dunning"),
    refetchOnWindowFocus: false,
  });
};

/**
 * GET /lime-admin/paid-sheet
 */
export const usePaidSheetRows = (query: GetPaidSheetRows["query"]) => {
  return useCustomQuery({
    queryKey: ["paidSheetRows", query],
    queryFn: () => get<GetPaidSheetRows>("/lime-admin/paid-sheet", query),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * GET /lime-admin/paid-sheet/comparison
 */
export const usePaidSheetComparison = (
  query: GetPaidSheetComparison["query"],
) => {
  return useCustomQuery({
    queryKey: ["paidSheetComparison", query],
    queryFn: () =>
      get<GetPaidSheetComparison>("/lime-admin/paid-sheet/comparison", query),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * GET /lime-admin/paid-sheet/activation-preview
 *
 * Reads eRačuni's partner lists, so it only runs when asked for.
 */
export const usePaidSheetActivationPreview = (
  clientIds: number[],
  enabled: boolean,
) => {
  return useCustomQuery({
    queryKey: ["paidSheetActivationPreview", clientIds],
    queryFn: () =>
      get<GetPaidSheetActivationPreview>(
        "/lime-admin/paid-sheet/activation-preview",
        { clientIds },
      ),
    enabled: enabled && clientIds.length > 0,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * GET /lime-admin/paid-sheet/[sheetRowId]/billing-prefill
 */
export const usePaidSheetBillingPrefill = (sheetRowId: number | null) => {
  return useCustomQuery({
    queryKey: ["paidSheetBillingPrefill", sheetRowId],
    queryFn: () =>
      get<GetPaidSheetBillingPrefill>(
        // Only runs when `enabled`; the assertion keeps the path's
        // template-literal type intact.
        `/lime-admin/paid-sheet/${sheetRowId!}/billing-prefill`,
      ),
    enabled: sheetRowId != null,
    refetchOnWindowFocus: false,
  });
};

/**
 * POST /lime-admin/paid-sheet/[sheetRowId]/refetch
 */
export const useRefetchPaidSheetRow = () => {
  return useCustomMutation({
    mutationFn: ({ sheetRowId }: { sheetRowId: number }) =>
      post<PostPaidSheetRefetch>(
        `/lime-admin/paid-sheet/${sheetRowId}/refetch`,
        {},
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["paidSheetComparison"] }),
  });
};

/**
 * PUT /lime-admin/paid-sheet/[sheetRowId]
 */
export const useReviewPaidSheetRow = () => {
  return useCustomMutation({
    mutationFn: ({
      sheetRowId,
      body,
    }: {
      sheetRowId: number;
      body: PutPaidSheetReview["body"];
    }) => put<PutPaidSheetReview>(`/lime-admin/paid-sheet/${sheetRowId}`, body),
  });
};

/**
 * PUT /lime-admin/paid-sheet/:sheetRowId/note
 */
export const useSetPaidSheetNote = () => {
  return useCustomMutation({
    mutationFn: ({
      sheetRowId,
      note,
    }: {
      sheetRowId: number;
      note: string | null;
    }) =>
      put<PutPaidSheetNote>(`/lime-admin/paid-sheet/${sheetRowId}/note`, {
        note,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["paidSheetComparison"] }),
  });
};

/**
 * POST /lime-admin/paid-sheet/import
 */
export const useImportPaidSheet = () => {
  return useCustomMutation({
    mutationFn: () =>
      post<PostPaidSheetImport>("/lime-admin/paid-sheet/import", {}),
  });
};

/**
 * POST /lime-admin/paid-sheet/rematch
 */
export const useRematchPaidSheet = () => {
  return useCustomMutation({
    mutationFn: () =>
      post<PostPaidSheetRematch>("/lime-admin/paid-sheet/rematch", {}),
  });
};

/**
 * POST /lime-admin/paid-sheet/decisions
 */
export const useDecidePaidSheetFields = () => {
  return useCustomMutation({
    mutationFn: (body: PostPaidSheetDecisions["body"]) =>
      post<PostPaidSheetDecisions>("/lime-admin/paid-sheet/decisions", body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["paidSheetComparison"] }),
  });
};

/**
 * POST /lime-admin/paid-sheet/sync
 */
export const useSyncPaidSheetToSheet = () => {
  return useCustomMutation({
    mutationFn: (body: PostPaidSheetSync["body"]) =>
      post<PostPaidSheetSync>("/lime-admin/paid-sheet/sync", body),
    // Awaited so the mutation stays pending until the re-read comparison is in.
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["paidSheetActivationPreview"],
      });
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ["paidSheetComparison"] }),
        queryClient.invalidateQueries({ queryKey: ["paidSheetRows"] }),
      ]);
    },
  });
};

/**
 * PUT /lime-admin/billing-gaps/[clientId]
 */
export const useUpdateBillingGapReview = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: number;
      body: PutBillingGapReview["body"];
    }) =>
      put<PutBillingGapReview>(`/lime-admin/billing-gaps/${clientId}`, body),
  });
};

/**
 * GET /lime-admin/client/[clientId]
 */
export const useClientDetails = (
  // query: GetClientDetails["query"],
  clientId: number,
) => {
  return useCustomQuery({
    queryKey: ["clientDetails", clientId],
    queryFn: () => get<GetClientDetails>(`/lime-admin/client/${clientId}`),
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
};

/**
 * PUT /lime-admin/client/[clientId]
 */
export const useUpdateClientDetails = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: number;
      body: PutClientDetails["body"];
    }) => put<PutClientDetails>(`/lime-admin/client/${clientId}`, body),
  });
};

export const useGetClientOverdueBanner = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["clientOverdueBanner", clientId],
    queryFn: () =>
      get<GetClientOverdueBanner>(
        `/lime-admin/client/${clientId}/overdue-banner`,
      ),
  });
};

export const usePutClientOverdueBanner = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: number;
      body: PutClientOverdueBanner["body"];
    }) =>
      put<PutClientOverdueBanner>(
        `/lime-admin/client/${clientId}/overdue-banner`,
        body,
      ),
  });
};

/**
 * GET /lime-admin/client/[clientId]/features
 */
export const useAdminClientFeatures = ({
  clientId,
  // Optional: evaluate "included in package" against a tier the client is not
  // on yet, which the close flow needs while the package is still being picked.
  pricingTierId,
}: AdminGetClientFeatures["params"] & { pricingTierId?: string }) => {
  return useCustomQuery({
    queryKey: ["adminClientFeatures", clientId, pricingTierId ?? null],
    queryFn: () =>
      get<AdminGetClientFeatures>(
        `/lime-admin/client/${clientId}/features`,
        pricingTierId ? { pricingTierId } : undefined,
      ),
  });
};

/**
 * PUT /lime-admin/client/[clientId]/features
 */
export const useSaveClientFeatures = (clientId: number) => {
  return useCustomMutation({
    mutationFn: (body: PutClientFeatures["body"]) =>
      put<PutClientFeatures>(`/lime-admin/client/${clientId}/features`, body),
  });
};

/**
 * GET /lime-admin/client/[clientId]/impersonation-sessions
 */
export const useClientImpersonationSessions = (
  clientId: number,
  query: GetClientImpersonationSessions["query"],
  enabled = true,
) => {
  return useCustomQuery({
    queryKey: ["clientImpersonationSessions", clientId, query],
    queryFn: () =>
      get<GetClientImpersonationSessions>(
        `/lime-admin/client/${clientId}/impersonation-sessions`,
        query,
      ),
    enabled,
  });
};

/**
 * GET /lime-admin/client/[clientId]/impersonation-sessions/[sessionId]/activity
 *
 * Lazily pulls the Axiom request log for one session; disabled until a session
 * is opened.
 */
export const useImpersonationSessionActivity = (
  clientId: number,
  sessionId: string | null,
) => {
  return useCustomQuery({
    queryKey: ["impersonationSessionActivity", clientId, sessionId],
    queryFn: () =>
      get<GetImpersonationSessionActivity>(
        // Only runs when `enabled` (sessionId != null); the assertion keeps the
        // path's template-literal type intact.
        `/lime-admin/client/${clientId}/impersonation-sessions/${sessionId!}/activity`,
      ),
    enabled: sessionId != null,
    gcTime: 0,
  });
};

/**
 * POST /lime-admin/client/[clientId]/login
 */
export const useLoginAsAdmin = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      ...body
    }: { clientId: number } & PostLoginAsAdmin["body"]) =>
      post<PostLoginAsAdmin>(`/lime-admin/client/${clientId}/login`, body),
  });
};

export const useDeleteFormBackgroundImage = () => {
  return useCustomMutation({
    mutationFn: (props: { params: DeleteFormBackgroundImage["params"] }) =>
      del(
        `/lime-admin/client/${props.params.clientId}/form/${props.params.formId}/image`,
      ),
  });
};

export const useUploadClientLogo = () => {
  return useCustomMutation({
    mutationFn: (props: { body: FormData; params: PostClientLogo["params"] }) =>
      post(
        `/lime-admin/client/${props.params.clientId}/logo`,
        props.body as unknown as { image: string }, // no bueno ampak bueno
      ),
  });
};

export const useUploadFormBackgroundImage = () => {
  return useCustomMutation({
    mutationFn: (props: {
      body: FormData;
      params: PostFormBackgroundImage["params"];
    }) =>
      post(
        `/lime-admin/client/${props.params.clientId}/form/${props.params.formId}/image`,
        props.body as unknown as { image: string }, // no bueno ampak bueno
      ),
  });
};

/**
 * GET /lime-admin/clients
 */
export const useClientNotifications = (
  clientId: GetClientNotifications["params"]["clientId"],
  query: GetClientNotifications["query"],
) => {
  return useCustomQuery({
    queryKey: ["clientNotifications", clientId, query],
    queryFn: () =>
      get<GetClientNotifications>(
        `/lime-admin/client/${clientId}/notifications`,
        query,
      ),
    placeholderData: keepPreviousData,
    gcTime: 0,
  });
};

/**
 * GET /lime-admin/client/[clientId]/notifications/bulk
 */
export const useClientBulkNotifications = (
  clientId: GetClientBulkNotifications["params"]["clientId"],
  query: GetClientBulkNotifications["query"],
  options?: { displayNotification?: boolean },
) => {
  return useCustomQuery({
    queryKey: ["clientBulkNotifications", clientId, query],
    queryFn: () =>
      get<GetClientBulkNotifications>(
        `/lime-admin/client/${clientId}/notifications/bulk`,
        query,
      ),
    placeholderData: keepPreviousData,
    gcTime: 0,
    displayNotification: options?.displayNotification,
  });
};

/**
 * GET /lime-admin/client/[clientId]/notifications/bulk/day
 *
 * Lazily fetches a single day's recipient rows; disabled until a day is
 * selected (expanded).
 */
export const useClientBulkNotificationsDay = (
  clientId: GetClientBulkNotificationsDay["params"]["clientId"],
  query: GetClientBulkNotificationsDay["query"] | null,
  options?: { displayNotification?: boolean },
) => {
  return useCustomQuery({
    queryKey: ["clientBulkNotificationsDay", clientId, query],
    queryFn: () =>
      get<GetClientBulkNotificationsDay>(
        `/lime-admin/client/${clientId}/notifications/bulk/day`,
        query ?? undefined,
      ),
    enabled: query != null,
    placeholderData: keepPreviousData,
    gcTime: 0,
    displayNotification: options?.displayNotification,
  });
};

/**
 * GET /lime-admin/client/[clientId]/notifications/bulk/[notificationLogId]
 *
 * Lazily fetches one bulk message's content + delivery timeline; disabled
 * until a message is opened.
 */
export const useClientBulkNotificationDetail = (
  clientId: GetClientBulkNotificationDetail["params"]["clientId"],
  notificationLogId: number | null,
  options?: { displayNotification?: boolean },
) => {
  return useCustomQuery({
    queryKey: ["clientBulkNotificationDetail", clientId, notificationLogId],
    queryFn: () =>
      get<GetClientBulkNotificationDetail>(
        // Only runs when `enabled` (notificationLogId != null); the assertion
        // keeps the path's template-literal type as `.../${number}`.
        `/lime-admin/client/${clientId}/notifications/bulk/${notificationLogId!}`,
      ),
    enabled: notificationLogId != null,
    gcTime: 0,
    displayNotification: options?.displayNotification,
  });
};

/**
 * POST /lime-admin/client/[clientId]/notifications/bulk/resend
 */
export const useResendClientBulkNotifications = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: number;
      body: PostClientBulkNotificationsResend["body"];
    }) =>
      post<PostClientBulkNotificationsResend>(
        `/lime-admin/client/${clientId}/notifications/bulk/resend`,
        body,
      ),
    displayNotification: true,
    onSuccess: (_data, { clientId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["clientBulkNotifications", clientId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["clientBulkNotificationsDay", clientId],
      });
    },
  });
};

export const useUpdateClientNotifications = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: number;
      body: PutClientNotifications["body"];
    }) =>
      put<PutClientNotifications>(
        `/lime-admin/client/${clientId}/notifications`,
        body,
      ),
  });
};

/**
 * GET /lime-admin/client/[clientId]/form
 */
export const useClientForms = (
  clientId: GetClientForms["params"]["clientId"],
) => {
  return useCustomQuery({
    queryKey: ["clientForms", clientId],
    queryFn: () => get<GetClientForms>(`/lime-admin/client/${clientId}/form`),
    placeholderData: keepPreviousData,
  });
};

/**
 * GET /lime-admin/client/[clientId]/form/[formId]
 */
export const useClientForm = ({
  clientId,
  formId,
}: {
  clientId: GetClientForm["params"]["clientId"];
  formId: GetClientForm["params"]["formId"];
}) => {
  return useCustomQuery({
    queryKey: ["clientForm", clientId, formId],
    queryFn: () =>
      get<GetClientForm>(`/lime-admin/client/${clientId}/form/${formId}`),
    gcTime: 0,
  });
};

/**
 * PUT /lime-admin/client/[clientId]/form/[formId]
 */
export const useUpdateClientForm = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      params,
    }: {
      body: PutClientForm["body"];
      params: PutClientForm["params"];
    }) =>
      put<PutClientForm>(
        `/lime-admin/client/${params.clientId}/form/${params.formId}`,
        body,
      ),
  });
};

/**
 * DELETE /lime-admin/client/[clientId]/form/[formId]
 */
export const useDeleteClientForm = () => {
  return useCustomMutation({
    mutationFn: ({ clientId, formId }: PutClientForm["params"]) =>
      del<PutClientForm>(`/lime-admin/client/${clientId}/form/${formId}`),
  });
};

/**
 * POST /lime-admin/client/[clientId]/form/
 */
export const useCreateClientForm = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      formHash,
    }: {
      clientId: PostClientForm["params"]["clientId"];
      formHash: PostClientForm["body"]["formHash"];
    }) =>
      post<PostClientForm>(`/lime-admin/client/${clientId}/form`, { formHash }),
  });
};

/**
 * POST /lime-admin/client/[clientId]/form/[formId]/duplicate
 */
export const useDuplicateClientForm = () => {
  return useCustomMutation({
    mutationFn: ({ clientId, formId }: PostDuplicateClientForm["params"]) =>
      post<PostDuplicateClientForm>(
        `/lime-admin/client/${clientId}/form/${formId}/duplicate`,
      ),
  });
};

export const useGetTimeOffs = (query: LimeAdminGetTimeOffs["query"]) => {
  return useCustomQuery({
    queryKey: ["TimeOffs", query],
    queryFn: () => get<LimeAdminGetTimeOffs>("/lime-admin/timeoff", query),
    placeholderData: keepPreviousData,
  });
};

export const useDeleteTimeOffs = () => {
  return useCustomMutation({
    mutationFn: (body: DeleteTimeOffs["body"]) =>
      del<DeleteTimeOffs>("/lime-admin/timeoff", body),
  });
};

export const useValidateServiceData = () => {
  return useCustomMutation({
    mutationFn: (body: PostValidateServiceData["body"]) =>
      post<PostValidateServiceData>(
        "/lime-admin/import/validateServiceData",
        body,
      ),
  });
};

export const useImportServices = () => {
  return useCustomMutation({
    mutationFn: (body: PostImportServices["body"]) =>
      post<PostImportServices>("/lime-admin/import/importServices", body),
  });
};

export const useImportCustomers = () => {
  return useCustomMutation({
    mutationFn: (body: PostImportCustomers["body"]) =>
      post<PostImportCustomers>("/lime-admin/import/importCustomers", body),
  });
};

export const useImportProducts = () => {
  return useCustomMutation({
    mutationFn: (body: PostImportProducts["body"]) =>
      post<PostImportProducts>("/lime-admin/import/importProducts", body),
  });
};

/**
 * GET /lime-admin/client/[clientId]/location
 */
export const useClientLocations = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["clientLocations", clientId],
    queryFn: () =>
      get<GetClientLocations>(`/lime-admin/client/${clientId}/location`),
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

export const useExportClientAppointments = () => {
  return useCustomMutation({
    mutationFn: async ({
      clientId,
      query,
    }: {
      clientId: number;
      query: GetClientAppointmentExport["query"];
    }) => {
      const response = await get<GetClientAppointmentExport>(
        `/lime-admin/client/${clientId}/appointment/export`,
        query,
      );

      if (!response?.data) {
        throw new Error("Appointment export returned no data");
      }

      let binaryString: string;
      try {
        binaryString = atob(response.data);
      } catch {
        throw new Error("Failed to decode CSV data");
      }
      const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));

      const blob = new Blob([bytes], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = response.filename || "appointments.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return response;
    },
  });
};

/**
 * GET /lime-admin/client/[clientId]/billing/change-log
 */
export const useGetBillingChangeLog = (
  clientId: number,
  query: GetBillingChangeLog["query"],
) => {
  return useCustomQuery({
    queryKey: ["billing-change-log", clientId, query],
    queryFn: () =>
      get<GetBillingChangeLog>(
        `/lime-admin/client/${clientId}/billing/change-log`,
        query,
      ),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

/**
 * PUT /lime-admin/client/[clientId]/billing/change-log/[changeSetId]/note
 */
export const usePutBillingChangeSetNote = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      changeSetId,
      body,
    }: {
      clientId: number;
      changeSetId: number;
      body: PutBillingChangeSetNote["body"];
    }) =>
      put<PutBillingChangeSetNote>(
        `/lime-admin/client/${clientId}/billing/change-log/${changeSetId}/note`,
        body,
      ),
    displayNotification: true,
  });
};

export const useGetTotalPrice = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["total-price", clientId],
    queryFn: () =>
      get<GetTotalPrice>(`/lime-admin/client/${clientId}/price-total`),
  });
};

export const useGetPricingTiers = () => {
  return useCustomQuery({
    queryKey: ["pricing-tiers"],
    queryFn: () => get<GetPricingTier>("/lime-admin/pricing-tier"),
  });
};

export const useGetPricingTier = (pricingTierId: string | undefined) => {
  return useCustomQuery({
    queryKey: ["pricing-tier", pricingTierId],
    queryFn: () =>
      get<GetPricingTierById>(`/lime-admin/pricing-tier/${pricingTierId}`),
    enabled: !!pricingTierId,
  });
};

export const usePutPricingTier = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      pricingTierId,
    }: {
      body: PutPricingTier["body"];
      pricingTierId: string;
    }) =>
      put<PutPricingTier>(`/lime-admin/pricing-tier/${pricingTierId}`, body),
  });
};

/**
 * ERACUNI
 */
export const useGetPartners = () => {
  return useCustomQuery({
    queryKey: ["eracuni-partners"],
    queryFn: () => get<GetPartners>("/lime-admin/eracuni/partner"),
    // placeholderData: keepPreviousData,
  });
};

export const useGetClientBilling = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["client-billing", clientId],
    queryFn: () =>
      get<GetClientBilling>(`/lime-admin/client/${clientId}/billing`),
    // placeholderData: keepPreviousData,
  });
};

export const usePutClientBilling = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      params,
    }: {
      body: PutClientBilling["body"];
      params: PutClientBilling["params"];
    }) =>
      put<PutClientBilling>(
        `/lime-admin/client/${params.clientId}/billing`,
        body,
      ),
  });
};

export const useCloseClient = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      clientId,
    }: {
      body: PostCloseClient["body"];
      clientId: number;
    }) => post<PostCloseClient>(`/lime-admin/client/${clientId}/close`, body),
    displayNotification: true,
  });
};

/**
 * POST /lime-admin/client/[clientId]/close/backdated
 */
export const useBackdatedClose = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      clientId,
    }: {
      body: PostBackdatedClose["body"];
      clientId: number;
    }) =>
      post<PostBackdatedClose>(
        `/lime-admin/client/${clientId}/close/backdated`,
        body,
      ),
    displayNotification: true,
  });
};

/**
 * POST /lime-admin/client/[clientId]/close/date
 */
export const useSetCloseDate = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      clientId,
    }: {
      body: PostCloseDate["body"];
      clientId: number;
    }) =>
      post<PostCloseDate>(`/lime-admin/client/${clientId}/close/date`, body),
    displayNotification: true,
  });
};

export const useActivateBilling = () => {
  return useCustomMutation({
    mutationFn: ({ params }: { params: PostActivateBilling["params"] }) =>
      post<PostActivateBilling>(
        `/lime-admin/client/${params.clientId}/billing/activate`,
      ),
    displayNotification: true,
  });
};

export const useDeactivateBilling = () => {
  return useCustomMutation({
    mutationFn: ({ params }: { params: PostDeactivateBilling["params"] }) =>
      post<PostDeactivateBilling>(
        `/lime-admin/client/${params.clientId}/billing/deactivate`,
      ),
  });
};

export const useCreateTierPeriods = () => {
  return useCustomMutation({
    mutationFn: ({
      params,
      body,
    }: {
      params: PostClientTierPeriod["params"];
      body: PostClientTierPeriod["body"];
    }) =>
      post<PostClientTierPeriod>(
        `/lime-admin/client/${params.clientId}/billing/tier-period`,
        body,
      ),
  });
};

export const useDeleteTierPeriod = () => {
  return useCustomMutation({
    mutationFn: ({ params }: { params: DeleteTierPeriod["params"] }) =>
      del<DeleteTierPeriod>(
        `/lime-admin/client/${params.clientId}/tier-period/${params.tierPeriodId}`,
      ),
    // Deleting a promo period unlocks the client's sign-up code.
    onSuccess: (_data, { params }) => {
      queryClient.invalidateQueries({
        queryKey: ["client-applied-promo-code", params.clientId],
      });
    },
  });
};

export const useUpdateTierPeriod = () => {
  return useCustomMutation({
    mutationFn: ({
      params,
      body,
    }: {
      params: PutTierPeriod["params"];
      body: PutTierPeriod["body"];
    }) =>
      put<PutTierPeriod>(
        `/lime-admin/client/${params.clientId}/tier-period/${params.tierPeriodId}`,
        body,
      ),
  });
};

export const useGetClientInvoices = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["client-invoices", clientId],
    queryFn: () =>
      get<GetClientInvoices>(`/lime-admin/client/${clientId}/invoice`),
  });
};

export const usePutClientUrls = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      params,
    }: {
      body: PutClientUrls["body"];
      params: PutClientUrls["params"];
    }) =>
      put<PutClientUrls>(`/lime-admin/client/${params.clientId}/urls`, body),
  });
};

export const useGetClientBillingDetails = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["client-billing-details", clientId],
    queryFn: () =>
      get<GetClientBillingDetails>(
        `/lime-admin/client/${clientId}/billing/details`,
      ),
  });
};

export const useGetClientNextInvoice = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["client-next-invoice", clientId],
    queryFn: () =>
      get<GetClientNextInvoice>(
        `/lime-admin/client/${clientId}/billing/next-invoice`,
      ),
  });
};

export const useGetInvoiceDetails = ({
  clientId,
  invoiceId,
}: {
  clientId: number;
  invoiceId: number;
}) => {
  return useCustomQuery({
    queryKey: ["invoice-details", clientId, invoiceId],
    queryFn: () =>
      get<GetClientInvoice>(
        `/lime-admin/client/${clientId}/invoice/${invoiceId}`,
      ),
  });
};

export const useDeleteClientInvoice = () => {
  return useCustomMutation({
    mutationFn: ({ params }: { params: DeleteClientInvoice["params"] }) =>
      del<DeleteClientInvoice>(
        `/lime-admin/client/${params.clientId}/invoice/${params.invoiceId}`,
      ),
  });
};

// Registering moves the billing start date and the SMS cursor server side; the
// settings form saves its copy of the start date back, so it must not go stale.
function invalidateManualInvoiceQueries(clientId: number) {
  for (const key of [
    "client-billing",
    "client-next-invoice",
    "billing-change-log",
    "manual-invoice-candidates",
  ]) {
    void queryClient.invalidateQueries({ queryKey: [key, clientId] });
  }
  void queryClient.invalidateQueries({
    queryKey: ["clientsNeedingManualInvoice"],
  });
}

export const useGetManualInvoiceCandidates = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["manual-invoice-candidates", clientId],
    queryFn: () =>
      get<GetManualInvoiceCandidates>(
        `/lime-admin/client/${clientId}/billing/manual-invoice/candidates`,
      ),
  });
};

export const useGetManualInvoiceProposal = (
  clientId: number,
  query: GetManualInvoiceProposal["query"] | null,
) => {
  return useCustomQuery({
    queryKey: ["manual-invoice-proposal", clientId, query],
    queryFn: () =>
      get<GetManualInvoiceProposal>(
        `/lime-admin/client/${clientId}/billing/manual-invoice/proposal`,
        query!,
      ),
    enabled: query != null,
    retry: false,
    // Keeps the form in place while a period or SMS change reloads it; a
    // different invoice starts blank rather than showing the last one.
    placeholderData: (previous, previousQuery) =>
      (previousQuery?.queryKey[2] as typeof query)?.number === query?.number
        ? previous
        : undefined,
  });
};

export const useRegisterManualInvoice = () => {
  return useCustomMutation({
    mutationFn: ({
      params,
      body,
    }: {
      params: PostManualInvoice["params"];
      body: PostManualInvoice["body"];
    }) =>
      post<PostManualInvoice>(
        `/lime-admin/client/${params.clientId}/billing/manual-invoice`,
        body,
      ),
    onSuccess: (_data, { params }) =>
      invalidateManualInvoiceQueries(params.clientId),
  });
};

export const useUnregisterManualInvoice = () => {
  return useCustomMutation({
    mutationFn: ({ params }: { params: DeleteManualInvoice["params"] }) =>
      del<DeleteManualInvoice>(
        `/lime-admin/client/${params.clientId}/billing/manual-invoice/${params.invoiceId}`,
      ),
    onSuccess: (_data, { params }) =>
      invalidateManualInvoiceQueries(params.clientId),
  });
};

export const useRequeueClientInvoice = () => {
  return useCustomMutation({
    mutationFn: ({
      params,
      releaseClaim,
    }: {
      params: PostRequeueClientInvoice["params"];
      /** Consent to re-issue an invoice whose last attempt left eRacuni's
       * outcome unknown. The server refuses without it. */
      releaseClaim?: boolean;
    }) =>
      post<PostRequeueClientInvoice>(
        `/lime-admin/client/${params.clientId}/invoice/${params.invoiceId}/requeue`,
        { releaseClaim },
      ),
  });
};

export const useRunRecurrentBilling = (options?: {
  onSuccess?: (data: PostRunRecurrentBilling["response"]) => void;
}) => {
  return useCustomMutation({
    mutationFn: () =>
      post<PostRunRecurrentBilling>(
        "/lime-admin/billing/recurrent-billing/run",
      ),
    displayNotification: true,
    onSuccess: options?.onSuccess,
  });
};

export const useGetInvoices = (query: GetInvoices["query"]) => {
  return useCustomQuery({
    queryKey: ["invoices", query],
    queryFn: () => get<GetInvoices>("/lime-admin/billing/invoice", query),
    placeholderData: keepPreviousData,
  });
};

export const useGetEmailActivity = (query: GetEmailActivity["query"]) => {
  return useCustomQuery({
    queryKey: ["email-activity", query],
    queryFn: () => get<GetEmailActivity>("/lime-admin/email-activity", query),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const usePostRecoverUser = () => {
  return useCustomMutation({
    mutationFn: ({ userId }: { userId: number }) =>
      post<PostAdminRecoverUser>(`/lime-admin/user/${userId}/recover`),
    displayNotification: true,
  });
};

export const usePostRecoverLocation = () => {
  return useCustomMutation({
    mutationFn: ({ locationId }: { locationId: number }) =>
      post<PostAdminRecoverLocation>(
        `/lime-admin/location/${locationId}/recover`,
      ),
    displayNotification: true,
  });
};

export const useGetShifts = (
  query: Omit<GetLimeAdminShifts["query"], "locationId" | "userId"> & {
    locationId?: number;
    userId?: number;
  },
) => {
  return useCustomQuery({
    queryKey: ["shifts", query],
    queryFn: () =>
      get<GetLimeAdminShifts>("/lime-admin/shift", {
        ...query,
        userId: query.userId!,
        locationId: query.locationId!,
      }),
    enabled: !!query.locationId && !!query.userId,
  });
};

export const useDeleteShifts = () => {
  return useCustomMutation({
    mutationFn: (query: DeleteLimeAdminShifts["query"]) =>
      del<DeleteLimeAdminShifts>("/lime-admin/shift", undefined, query),
  });
};

export const useDeletePartner = () => {
  return useCustomMutation({
    mutationFn: ({ params }: { params: DeleteClientPartnerId["params"] }) =>
      del<DeleteClientPartnerId>(
        `/lime-admin/client/${params.clientId}/partnerId`,
      ),
    displayNotification: true,
  });
};

export const useGetLimeAdminTaxRegisterSettings = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["lime-admin-tax-register-settings", clientId],
    queryFn: () =>
      get<GetLimeAdminTaxRegisterSettings>(
        `/lime-admin/client/${clientId}/tax-register/settings`,
      ),
  });
};

export const usePostLimeAdminTaxRegisterSettings = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: number;
      body: PostLimeAdminTaxRegisterSettings["body"];
    }) =>
      post<PostLimeAdminTaxRegisterSettings>(
        `/lime-admin/client/${clientId}/tax-register/settings`,
        body,
      ),
  });
};

export const usePostQuickClientOnboarding = () => {
  return useCustomMutation({
    mutationFn: (body: PostQuickClientOnboarding["body"]) =>
      post<PostQuickClientOnboarding>("/lime-admin/import/onboarding", body),
    displayNotification: true,
  });
};

/**
 * GET /lime-admin/client/[clientId]/tag-combination
 */
export const useGetTagCombinations = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["tag-combinations", clientId],
    queryFn: () =>
      get<GetLimeAdminTagCombinations>(
        `/lime-admin/client/${clientId}/tag-combination`,
      ),
  });
};

/**
 * POST /lime-admin/client/[clientId]/tag-combination
 */
export const useCreateTagCombination = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: number;
      body: PostLimeAdminTagCombination["body"];
    }) =>
      post<PostLimeAdminTagCombination>(
        `/lime-admin/client/${clientId}/tag-combination`,
        body,
      ),
    displayNotification: true,
  });
};

/**
 * DELETE /lime-admin/client/[clientId]/tag-combination/[combinationId]
 */
export const useDeleteTagCombination = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      combinationId,
    }: {
      clientId: number;
      combinationId: number;
    }) =>
      del<DeleteLimeAdminTagCombination>(
        `/lime-admin/client/${clientId}/tag-combination/${combinationId}`,
      ),
    displayNotification: true,
  });
};

/**
 * PUT /lime-admin/client/[clientId]/tag-combination/[combinationId]/order
 */
export const useUpdateTagCombinationOrder = () => {
  return useCustomMutation({
    mutationFn: ({
      clientId,
      combinationId,
      body,
    }: {
      clientId: number;
      combinationId: number;
      body: PutLimeAdminTagCombinationOrder["body"];
    }) =>
      put<PutLimeAdminTagCombinationOrder>(
        `/lime-admin/client/${clientId}/tag-combination/${combinationId}/order`,
        body,
      ),
    displayNotification: true,
  });
};

/**
 * POST /lime-admin/broadcast
 */
export const useCreateBroadcast = () => {
  return useCustomMutation({
    mutationFn: (body: PostLimeAdminBroadcast["body"]) =>
      post<PostLimeAdminBroadcast>("/lime-admin/broadcast", body),
    displayNotification: true,
  });
};

/**
 * GET /lime-admin/broadcast
 */
export const useBroadcastList = (
  query: GetLimeAdminBroadcasts["query"] = { page: 1, pageSize: 20 },
) => {
  return useCustomQuery({
    queryKey: ["broadcasts", query],
    queryFn: () => get<GetLimeAdminBroadcasts>("/lime-admin/broadcast", query),
    placeholderData: keepPreviousData,
  });
};

/**
 * GET /lime-admin/broadcast/:broadcastId
 */
export const useBroadcastDetail = (broadcastId: number) => {
  return useCustomQuery({
    queryKey: ["broadcast", broadcastId],
    queryFn: () =>
      get<GetLimeAdminBroadcastById>(`/lime-admin/broadcast/${broadcastId}`),
    enabled: broadcastId > 0,
  });
};

/**
 * POST /lime-admin/client/:clientId/complete-scrape
 */
export const useCompleteScrape = () => {
  return useCustomMutation({
    mutationFn: ({ clientId }: { clientId: number }) =>
      post<PostCompleteScrape>(
        `/lime-admin/client/${clientId}/complete-scrape`,
        {},
      ),
    displayNotification: true,
  });
};

/**
 * POST /lime-admin/client/:clientId/reset-scrape-acceptance
 */
export const useResetScrapeAcceptance = () => {
  return useCustomMutation({
    mutationFn: ({ clientId }: { clientId: number }) =>
      post<PostResetScrapeAcceptance>(
        `/lime-admin/client/${clientId}/reset-scrape-acceptance`,
        {},
      ),
    displayNotification: true,
  });
};

/**
 * GET /lime-admin/hubspot/deals
 */
export const useGetLimeAdminHubspotDeals = (
  query: GetLimeAdminHubspotDeals["query"],
  enabled?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["lime-admin-hubspot-deals", query.q],
    queryFn: () =>
      get<GetLimeAdminHubspotDeals>("/lime-admin/hubspot/deals", query),
    enabled: enabled ?? query.q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
};

/**
 * GET /lime-admin/client/:clientId/promo-code
 */
export const useClientPromoCodes = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["client-promo-codes", clientId],
    queryFn: () =>
      get<GetLimeAdminClientPromoCodes>(
        `/lime-admin/client/${clientId}/promo-code`,
      ),
  });
};

/**
 * POST /lime-admin/client/:clientId/promo-code
 */
export const useCreateClientPromoCode = () => {
  return useCustomMutation({
    mutationFn: ({
      params,
      body,
    }: {
      params: PostLimeAdminClientPromoCode["params"];
      body: PostLimeAdminClientPromoCode["body"];
    }) =>
      post<PostLimeAdminClientPromoCode>(
        `/lime-admin/client/${params.clientId}/promo-code`,
        body,
      ),
    displayNotification: true,
  });
};

/**
 * PUT /lime-admin/client/:clientId/promo-code/:promoCodeId
 */
export const useUpdateClientPromoCode = () => {
  return useCustomMutation({
    mutationFn: ({
      params,
      body,
    }: {
      params: PutLimeAdminClientPromoCode["params"];
      body: PutLimeAdminClientPromoCode["body"];
    }) =>
      put<PutLimeAdminClientPromoCode>(
        `/lime-admin/client/${params.clientId}/promo-code/${params.promoCodeId}`,
        body,
      ),
    onSuccess: (_data, { params }) => {
      queryClient.invalidateQueries({
        queryKey: ["client-promo-codes", params.clientId],
      });
    },
    displayNotification: true,
  });
};

/**
 * GET /lime-admin/client/:clientId/applied-promo-code
 */
export const useClientAppliedPromoCode = (clientId: number) => {
  return useCustomQuery({
    queryKey: ["client-applied-promo-code", clientId],
    queryFn: () =>
      get<GetLimeAdminClientAppliedPromoCode>(
        `/lime-admin/client/${clientId}/applied-promo-code`,
      ),
  });
};

/**
 * PUT /lime-admin/client/:clientId/applied-promo-code
 */
export const useSetClientAppliedPromoCode = () => {
  return useCustomMutation({
    mutationFn: ({
      params,
      body,
    }: {
      params: PutLimeAdminClientAppliedPromoCode["params"];
      body: PutLimeAdminClientAppliedPromoCode["body"];
    }) =>
      put<PutLimeAdminClientAppliedPromoCode>(
        `/lime-admin/client/${params.clientId}/applied-promo-code`,
        body,
      ),
    onSuccess: (_data, { params }) => {
      queryClient.invalidateQueries({
        queryKey: ["client-applied-promo-code", params.clientId],
      });
      // Saving the code can book promo tier periods.
      queryClient.invalidateQueries({
        queryKey: ["client-billing", params.clientId],
      });
    },
    displayNotification: true,
  });
};

/**
 * DELETE /lime-admin/client/:clientId/promo-code/:promoCodeId
 */
export const useDeleteClientPromoCode = () => {
  return useCustomMutation({
    mutationFn: ({
      params,
    }: {
      params: DeleteLimeAdminClientPromoCode["params"];
    }) =>
      del<DeleteLimeAdminClientPromoCode>(
        `/lime-admin/client/${params.clientId}/promo-code/${params.promoCodeId}`,
      ),
    displayNotification: true,
  });
};
